import { ReactNode, useCallback, useMemo, useState } from 'react';

import { useInventoryDataCtx } from '@/providers';

import { EventModalView } from './InventoryProvider/EventModalView';
import { findIdForNameInInventoryTree } from './InventoryProvider/findIdForNameInInventoryTree.ts';
import { findNameForIdInInventoryTree } from './InventoryProvider/findNameForIdInInventoryTree.ts';
import { InventoryContext, InventoryContextType } from './InventoryProvider/InventoryContext';

interface InventoryProviderProps {
  children: ReactNode;
}

/**
 * InventoryProvider fetches and provides inventory data
 * Includes EventDetails modal for displaying event information, trogged by setEventId
 * @param children
 * @constructor
 */
export function InventoryProvider({ children }: InventoryProviderProps) {
  const { firstRootInventories: inventory, isFetching } = useInventoryDataCtx();
  const [eventId, setEventId] = useState<string>('');

  const getInventoryNameById = useCallback(
    (sourceId?: string): string => {
      if (!sourceId || !inventory.length) return 'n/a';

      const found = findNameForIdInInventoryTree(sourceId, inventory);
      return found ? found.name : 'n/a';
    },
    [inventory]
  );

  const getInventoryIdByName: (sourceName?: string | undefined) => string | undefined = useCallback(
    sourceName => {
      if (!sourceName || !inventory.length) return undefined;

      const found = findIdForNameInInventoryTree(sourceName, inventory);
      return found ? found.id : undefined;
    },
    [inventory]
  );

  const value: InventoryContextType = useMemo(() => {
    return {
      inventory,
      isFetching,
      getInventoryNameById,
      getInventoryIdByName,
      setEventId,
      closeModal: () => setEventId(''),
    };
  }, [inventory, isFetching, getInventoryNameById, getInventoryIdByName]);

  return (
    <InventoryContext value={value}>
      {children}
      <EventModalView eventId={eventId} setEventId={setEventId} />
    </InventoryContext>
  );
}
